import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useModulePermissions } from '../../context/ModulePermissionsContext';
import type { AuthUser, UserRole } from '../../types/user.types';
import { ForbiddenState } from '@/components/ui-legacy';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  /** Extra predicate — grants access when this returns true, regardless of role. */
  customCheck?: (user: AuthUser) => boolean;
  /** If set, the route also requires this module to be enabled for the user's role. */
  moduleKey?: string;
}

function LoadingScreen() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background">
      <div
        className="mb-4 size-10 animate-spin rounded-full border-4 border-primary border-t-transparent"
        role="status"
        aria-label="Loading"
      />
      <p className="font-heading text-lg font-semibold text-primary">Attendza</p>
    </div>
  );
}

export default function ProtectedRoute({ children, allowedRoles, customCheck, moduleKey }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { isModuleAllowed } = useModulePermissions();
  const location = useLocation();

  if (isLoading) return <LoadingScreen />;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (user) {
    // super_admin may only access their own admin area — redirect anything else to the tenants hub
    const isSuperAdminPath =
      location.pathname === '/admin/tenants' ||
      location.pathname === '/admin/permissions' ||
      location.pathname === '/admin/user-management' ||
      location.pathname === '/admin/audit-logs' ||
      location.pathname.startsWith('/admin/audit-logs/') ||
      location.pathname.startsWith('/admin/tenants/');
    if (user.role === 'super_admin' && !isSuperAdminPath) {
      return <Navigate to="/admin/tenants" replace />;
    }


    const roleOk = !allowedRoles || allowedRoles.includes(user.role);
    const customOk = customCheck ? customCheck(user) : false;
    if (!roleOk && !customOk) {
      return (
        <ForbiddenState
          fullscreen
          title="Access denied"
          description="You don't have permission to access this page. Contact HR if you believe this is an error."
        />
      );
    }

    // Module-level permission check (admin-configurable via the permissions matrix)
    if (moduleKey && !isModuleAllowed(moduleKey)) {
      return (
        <ForbiddenState
          fullscreen
          title="Module disabled"
          description="This module has been disabled for your role. Contact your Super Admin if you need access."
        />
      );
    }
  }

  return <>{children}</>;
}
